import React, { useState, useEffect } from 'react';

const BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

const TIERS = {
  emerging: {
    label: 'Emerging Palate',
    sub: 'Member · Foundation & Potential',
    greeting: 'Ready to explore your palate today?',
    color: '#4a7c3f',
    bg: '#f0f7ee',
    badge: '🌱',
    focus: 'Learn · Experience · Grow',
    badgeBg: 'linear-gradient(135deg, #5a9e4f, #3d6b35)',
  },
  silver: {
    label: 'Silver Palate',
    sub: 'Member · Skilled & Reliable',
    greeting: 'Your refined skills make a real difference.',
    color: '#5c6bc0',
    bg: '#f0f1ff',
    badge: '⭐',
    focus: 'Refine · Contribute · Impact',
    badgeBg: 'linear-gradient(135deg, #7986cb, #3949ab)',
  },
  gold: {
    label: 'Gold Palate',
    sub: 'Member · Expert & Leader',
    greeting: 'Your expertise shapes our products.',
    color: '#b8860b',
    bg: '#fffbf0',
    badge: '👑',
    focus: 'Lead · Innovate · Excel',
    badgeBg: 'linear-gradient(135deg, #d4ac0d, #b7950b)',
  },
  golden: {
    label: 'The Golden Palate',
    sub: 'Award · Highest Honor',
    greeting: 'A true benchmark of sensory excellence.',
    color: '#8B4513',
    bg: '#fff8f0',
    badge: '🏆',
    focus: 'Inspire · Lead · Benchmark',
    badgeBg: 'linear-gradient(135deg, #cd853f, #8B4513)',
  },
};

const ATTRS = [
  { key: 'appearance', icon: '', title: 'Appearance', desc: 'Color, visual appeal and presentation' },
  { key: 'aroma', icon: '', title: 'Aroma', desc: 'Fragrance and smell profile' },
  { key: 'texture', icon: '', title: 'Texture', desc: 'Crunch, chew and mouthfeel' },
  { key: 'taste', icon: '', title: 'Taste', desc: 'Flavor, balance and taste experience' },
  { key: 'aftertaste', icon: '', title: 'Aftertaste', desc: 'Lingering flavors and finish' },
  { key: 'overall', icon: '', title: 'Overall', desc: 'Overall impression and acceptability' },
];

export default function PanelistPortal() {
  const [screen, setScreen] = useState('login'); // login | home | scoring | done
  const [sessionAttrs, setSessionAttrs] = useState(ATTRS); // dynamic per-session
  const [textParams, setTextParams] = useState([]);
  const [textAnswers, setTextAnswers] = useState({}); // { sample_name: { param_key: text } }
  const [panelist, setPanelist] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [samples, setSamples] = useState([]);
  const [myScores, setMyScores] = useState([]);
  const [sampleIdx, setSampleIdx] = useState(0);
  const [attrIdx, setAttrIdx] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [histData, setHistData] = useState(null);
  const openHistory = async () => {
    try {
      const r = await fetch(BASE + '/panel/my-history/' + panelist.id).then(x => x.json());
      setHistData(r); setShowHistory(true);
    } catch (e) { alert('Could not load history'); }
  };
  const [scores, setScores] = useState({});
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const tier = panelist ? (TIERS[panelist.tier] || TIERS.emerging) : null;

  const login = async () => {
    setLoginError('');
    setSubmitting(true);
    try {
      const r = await fetch(BASE + '/panel/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      }).then(r => r.json());
      if (r.error) { setLoginError(r.error); setSubmitting(false); return; }
      setPanelist(r.panelist);
      loadSessions(r.panelist.id);
      setScreen('home');
    } catch(e) { setLoginError('Connection error'); }
    setSubmitting(false);
  };

  const loadSessions = async (pid) => {
    const r = await fetch(BASE + '/panel/sessions/' + pid).then(r => r.json());
    setSessions(r);
  };

  const DESC = { appearance: 'Color, visual appeal and presentation', aroma: 'Fragrance and smell profile', texture: 'Crunch, chew and mouthfeel', taste: 'Flavor, balance and taste experience', aftertaste: 'Lingering flavors and finish', overall: 'Overall impression and acceptability', value: 'Value for money' };
  const startSession = async (session) => {
    try {
      const pr = await fetch(BASE + '/panel/sessions/' + session.id + '/params').then(r => r.json());
      if (Array.isArray(pr) && pr.length) {
        const scoreP = pr.filter(p => p.param_type !== 'text');
        const textP = pr.filter(p => p.param_type === 'text');
        setSessionAttrs(scoreP.map(p => ({ key: p.param_key, icon: '', title: p.label, desc: DESC[p.param_key] || ('Rate: ' + p.label) })));
        setTextParams(textP);
      } else { setSessionAttrs(ATTRS); setTextParams([]); }
    } catch { setSessionAttrs(ATTRS); setTextParams([]); }
    setTextAnswers({});
    setActiveSession(session);
    const r = await fetch(BASE + '/panel/sessions/' + session.id + '/samples/' + panelist.id).then(r => r.json());
    setSamples(r.samples);
    setMyScores(r.myScores);
    // Init scores
    const init = {};
    r.samples.forEach(s => { init[s.sample_name] = { appearance: 0, aroma: 0, texture: 0, taste: 0, aftertaste: 0, overall: 0, comments: '', recommendation: '' }; });  // custom keys default undefined->0 via wizard
    // Pre-fill existing
    r.myScores.forEach(sc => {
      const sn = sc.sample_name || 'Sample A';
      if (!init[sn]) init[sn] = {};
      init[sn] = { appearance: sc.appearance, aroma: sc.aroma, texture: sc.texture, taste: sc.taste, aftertaste: sc.aftertaste, overall: sc.overall, comments: sc.comments||'', recommendation: sc.recommendation||'' };
    });
    setScores(init);
    setSampleIdx(0); setAttrIdx(0);
    setScreen('scoring');
  };

  const currentSample = samples[sampleIdx];
  const currentAttr = sessionAttrs[attrIdx];
  const currentScore = currentSample ? (scores[currentSample.sample_name] || {}) : {};
  const progress = ((attrIdx * samples.length + sampleIdx) / (samples.length * sessionAttrs.length)) * 100;

  const setAttrScore = (val) => {
    setScores(prev => ({
      ...prev,
      [currentSample.sample_name]: { ...prev[currentSample.sample_name], [currentAttr.key]: val }
    }));
  };

  const nextStep = async () => {
    // Attribute-first flow: score this attribute for ALL samples, then move to next attribute
    if (sampleIdx < samples.length - 1) {
      // Next sample, same attribute
      setSampleIdx(s => s + 1);
    } else if (attrIdx < sessionAttrs.length - 1) {
      // Last sample done for this attribute -> next attribute, back to first sample
      setAttrIdx(a => a + 1);
      setSampleIdx(0);
    } else {
      // All attributes for all samples done -> validate comments then save
      const missing = samples.find(smp => !((scores[smp.sample_name]?.comments) || '').trim());
      if (missing) {
        alert('Comments are required for ' + missing.sample_name + '. Please describe what you tasted before submitting.');
        setSampleIdx(samples.indexOf(missing));
        return;
      }
      const STD = ['appearance','aroma','texture','taste','aftertaste','overall','value','comments','recommendation'];
      for (const smp of samples) {
        const s = scores[smp.sample_name] || {};
        const custom_values = {};
        Object.keys(s).forEach(k => { if (!STD.includes(k)) custom_values[k] = s[k]; });
        Object.entries((textAnswers[smp.sample_name] || {})).forEach(([k, v]) => { if (v && String(v).trim()) custom_values[k] = v; });
        await fetch(BASE + '/panel/score', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: activeSession.id,
            panelist_id: panelist.id,
            panelist_name: panelist.name,
            sample_name: smp.sample_name,
            ...s,
            custom_values
          })
        });
      }
      setScreen('done');
    }
  };

  const prevStep = () => {
    if (sampleIdx > 0) setSampleIdx(s => s - 1);
    else if (attrIdx > 0) { setAttrIdx(a => a - 1); setSampleIdx(samples.length - 1); }
  };

  const S = { minHeight: '100vh', background: '#1a1a2e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: '-apple-system, Arial, sans-serif' };
  const CARD = { background: 'white', borderRadius: 20, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };

  // LOGIN
  if (screen === 'login') return (
    <div style={S}>
      <img src="/logo.png" alt="Tasting Folks" style={{ height: 48, marginBottom: 24, borderRadius: 8 }} onError={e => e.target.style.display='none'} />
      <div style={CARD}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#333' }}>Tasting Folks</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Sensory Club — Panel Login</div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 5 }}>Username</div>
          <input value={loginForm.username} onChange={e => setLoginForm({...loginForm, username: e.target.value})}
            placeholder="Your username" onKeyDown={e => e.key === 'Enter' && login()}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 5 }}>Password</div>
          <input type="password" value={loginForm.password} onChange={e => setLoginForm({...loginForm, password: e.target.value})}
            placeholder="Your password" onKeyDown={e => e.key === 'Enter' && login()}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {loginError && <div style={{ background: '#fce8e6', color: '#c62828', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{loginError}</div>}
        <button onClick={login} disabled={submitting}
          style={{ width: '100%', background: '#7B2D6B', color: 'white', border: 'none', borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          {submitting ? 'Signing in...' : 'Sign In →'}
        </button>
      </div>
    </div>
  );

  // HOME
  if (screen === 'home' && panelist) return (
    <div style={{ ...S, justifyContent: 'flex-start', paddingTop: 40, background: tier.bg }}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 500, marginBottom: 24 }}>
        <div style={{ background: 'white', borderRadius: 20, padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/logo.png" alt="Tasting Folks" style={{ height: 40 }} onError={e => e.target.style.display='none'} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#333' }}>Welcome, {panelist.name.split(' ')[0]}!</div>
            <div style={{ fontSize: 12, color: '#888' }}>{tier.greeting}</div>
          </div>
          {/* Tier badge image */}
          <div style={{ textAlign: 'center', width: 72 }}>
            <img src={'/tier_' + (panelist.tier || 'emerging') + '.png'} alt={tier.label}
              style={{ width: 64, height: 64, objectFit: 'contain' }}
              onError={e => { e.target.style.display='none'; }} />
            <div style={{ fontSize: 9, color: tier.color, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}>
              {panelist.tier === 'golden' ? 'Golden' : panelist.tier === 'gold' ? 'Gold' : panelist.tier === 'silver' ? 'Silver' : 'Emerging'}
            </div>
          </div>
        </div>
        {/* Tier info strip */}
        <div style={{ background: tier.badgeBg, borderRadius: 10, padding: '8px 16px', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11, fontWeight: 700 }}>{tier.label}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>{tier.focus}</div>
        </div>
      </div>

      {/* Sessions */}
      <div style={{ width: '100%', maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button onClick={openHistory} style={{ background: '#fff', border: '1.5px solid #7B2D6B', color: '#7B2D6B', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>📋 My History</button>
        </div>
        {showHistory && histData && (
          <div style={{ position: 'fixed', inset: 0, background: '#faf7f9', zIndex: 1200, overflowY: 'auto', padding: 16 }}>
            <div style={{ maxWidth: 560, margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#7B2D6B' }}>My Tasting History</div>
                <button onClick={() => setShowHistory(false)} style={{ background: '#7B2D6B', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>← Back</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
                {[['Sessions', histData.stats?.sessions], ['Samples', histData.stats?.samples_scored], ['My Avg', histData.stats?.avg_overall != null ? histData.stats.avg_overall : '—']].map(([lbl, val]) => (
                  <div key={lbl} style={{ background: '#fff', borderRadius: 12, padding: '10px 8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#999', fontWeight: 700 }}>{lbl}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#7B2D6B' }}>{val != null ? val : '—'}</div>
                  </div>
                ))}
              </div>
              {(histData.history || []).length === 0 && <div style={{ color: '#999', fontSize: 14, textAlign: 'center', marginTop: 30 }}>No tastings yet.</div>}
              {(histData.history || []).map(h => (
                <div key={h.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{h.product_name || 'Product'}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#7B2D6B' }}>{h.overall != null ? h.overall : '—'}<span style={{ fontSize: 10, color: '#999' }}>/10</span></div>
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{h.sample_name || 'Sample A'} · {h.session_code || ''} · {h.created_at ? String(h.created_at).slice(0, 10) : (h.session_date || '')}</div>
                  {h.recommendation && <div style={{ fontSize: 12, fontWeight: 600, color: '#E8541A' }}>{h.recommendation}</div>}
                  {h.comments && <div style={{ fontSize: 12, color: '#555', marginTop: 4, fontStyle: 'italic' }}>"{h.comments}"</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Open Sessions</div>
        {sessions.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 20, padding: 28, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <img src="/logo.png" alt="Tasting Folks" style={{ height: 52, marginBottom: 16, borderRadius: 8 }} onError={e => e.target.style.display='none'} />
            <div style={{ fontSize: 18, fontWeight: 800, color: '#333', marginBottom: 8 }}>
              {panelist.tier === 'golden' ? `Welcome, ${panelist.name.split(' ')[0]}.` :
               panelist.tier === 'gold' ? `Welcome, ${panelist.name.split(' ')[0]}.` :
               panelist.tier === 'silver' ? `Welcome back, ${panelist.name.split(' ')[0]}!` :
               `Welcome, ${panelist.name.split(' ')[0]}!`}
            </div>
            <div style={{ fontSize: 13, color: '#666', lineHeight: 1.7, maxWidth: 320, margin: '0 auto 20px' }}>
              {panelist.tier === 'golden' ? "True excellence needs no session to prove itself. Your extraordinary palate sets the benchmark at Tasting Folks. We will be calling on you very soon." :
               panelist.tier === 'gold' ? "Your expert palate is one of our most valuable assets. No sessions right now, but your leadership in the tasting room will be vital as ever." :
               panelist.tier === 'silver' ? "Your consistent feedback has made a real impact. No active sessions right now, but stay sharp — we will be back soon!" :
               "Your sensory journey is just beginning! Every session sharpens your palate. Check back soon — your next tasting adventure is on its way!"}
            </div>
            <img src="/progression.png" alt="Tasting Folks Progression" style={{ width: '100%', borderRadius: 12, marginBottom: 16 }} onError={e => e.target.style.display='none'} />
            <div style={{ background: tier.bg, borderRadius: 10, padding: '8px 16px', display: 'inline-block' }}>
              <div style={{ fontSize: 11, color: tier.color, fontWeight: 700 }}>{tier.badge} {tier.label} · {tier.focus}</div>
            </div>
          </div>
        ) : sessions.map(s => {
          const done = s.my_score_count > 0;
          return (
            <div key={s.id} style={{ background: 'white', borderRadius: 16, padding: 18, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, color: tier.color, fontWeight: 700 }}>{s.session_code}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginTop: 2 }}>{s.product_name}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.session_date} · {s.sample_count || 1} sample{s.sample_count > 1 ? 's' : ''}</div>
                </div>
                {done && <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>✓ Scored</span>}
              </div>
              <button onClick={() => startSession(s)}
                style={{ width: '100%', background: done ? '#f5f5f5' : tier.badgeBg, color: done ? '#666' : 'white', border: 'none', borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {done ? 'Re-score →' : 'Start Scoring →'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  // SCORING
  if (screen === 'scoring' && currentSample && currentAttr) return (
    <div style={S}>
      {/* Progress */}
      <div style={{ width: '100%', maxWidth: 440, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{currentSample.sample_name}{currentSample.trial_ref ? ' · ' + currentSample.trial_ref : ''}</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{currentAttr.title} ({attrIdx + 1}/{sessionAttrs.length})</span>
        </div>
        <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
          <div style={{ height: '100%', background: tier?.color || '#7B2D6B', borderRadius: 2, width: progress + '%', transition: 'width 0.3s' }} />
        </div>
        {samples.length > 1 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            {samples.map((s,i) => (
              <div key={s.sample_name} style={{ flex: 1, height: 3, borderRadius: 2, background: i < sampleIdx ? (tier?.color || '#7B2D6B') : i === sampleIdx ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)' }} />
            ))}
          </div>
        )}
      </div>

      <div style={CARD}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>{currentAttr.icon}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#333', marginBottom: 6 }}>{currentAttr.title}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{currentAttr.desc}</div>
        </div>

        {/* Score slider / buttons */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(v => (
              <button key={v} onClick={() => setAttrScore(v)}
                style={{ width: 34, height: 34, borderRadius: 8, border: 'none', background: currentScore[currentAttr.key] === v ? tier?.badgeBg || '#7B2D6B' : currentScore[currentAttr.key] >= v ? 'rgba(123,45,107,0.15)' : '#f5f5f5', color: currentScore[currentAttr.key] === v ? 'white' : '#666', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {v}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#aaa', padding: '0 2px' }}>
            <span>Poor</span><span>Excellent</span>
          </div>
        </div>

        {/* Last attr - recommendation + comment */}
        {attrIdx === sessionAttrs.length - 1 && (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Recommendation</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['accept','✓ Accept','#e8f5e9','#2e7d32'],['modify','⚙ Modify','#fff3e0','#e67e00'],['reject','✗ Reject','#fce8e6','#c62828']].map(([val,lbl,bg,col]) => (
                  <button key={val} onClick={() => setScores(prev => ({ ...prev, [currentSample.sample_name]: { ...prev[currentSample.sample_name], recommendation: val }}))}
                    style={{ flex:1, padding:8, borderRadius:8, border:`2px solid ${currentScore.recommendation===val?col:'#eee'}`, background:currentScore.recommendation===val?bg:'white', color:col, fontWeight:700, cursor:'pointer', fontSize:12 }}>{lbl}</button>
                ))}
              </div>
            </div>
            {textParams.map(tp => (
              <div key={tp.param_key} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>✳ {tp.label}</div>
                <textarea value={(textAnswers[currentSample.sample_name]?.[tp.param_key]) || ''}
                  onChange={e => setTextAnswers(prev => ({ ...prev, [currentSample.sample_name]: { ...(prev[currentSample.sample_name]||{}), [tp.param_key]: e.target.value } }))}
                  rows={2} placeholder={tp.label + '...'}
                  style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 10, fontSize: 13, resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>Comments <span style={{ color: '#c62828' }}>* required</span></div>
              <textarea value={currentScore.comments || ''} onChange={e => setScores(prev => ({ ...prev, [currentSample.sample_name]: { ...prev[currentSample.sample_name], comments: e.target.value}}))}
                rows={2} placeholder="Your observations — required before submitting" style={{ width: '100%', padding: '8px 12px', border: (currentScore.comments||'').trim() ? '1.5px solid #eee' : '1.5px solid #e57373', borderRadius: 10, fontSize: 13, resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          {(attrIdx > 0 || sampleIdx > 0) && (
            <button onClick={prevStep} style={{ flex:1, padding:14, background:'#f5f5f5', color:'#666', border:'none', borderRadius:12, fontSize:14, cursor:'pointer' }}>← Back</button>
          )}
          <button onClick={nextStep} disabled={!currentScore[currentAttr.key]}
            style={{ flex:2, padding:14, background: tier?.badgeBg || '#7B2D6B', color:'white', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', opacity: currentScore[currentAttr.key] ? 1 : 0.5 }}>
            {sampleIdx === samples.length - 1 && attrIdx === sessionAttrs.length - 1 ? '✓ Submit' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );

  // DONE
  if (screen === 'done') return (
    <div style={S}>
      <div style={CARD}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 60, marginBottom: 12 }}>{tier?.badge || '🎉'}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#333', marginBottom: 8 }}>Well done, {panelist?.name.split(' ')[0]}!</div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>Your expert feedback has been recorded. Thank you for contributing to product excellence.</div>
          <div style={{ background: tier?.bg || '#f9f3f8', borderRadius: 14, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{tier?.label}</div>
            <div style={{ fontSize: 12, color: tier?.color || '#7B2D6B', fontWeight: 700 }}>{tier?.focus}</div>
          </div>
          <button onClick={() => { setScreen('home'); loadSessions(panelist.id); }}
            style={{ width: '100%', background: tier?.badgeBg || '#7B2D6B', color: 'white', border: 'none', borderRadius: 12, padding: 14, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Back to Sessions
          </button>
        </div>
      </div>
    </div>
  );

  return null;
}
